/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Sidebar, tituloBase, usePermiso } from "../../../../shared";
import { Toast } from "primereact/toast";
import { Skeleton } from "primereact/skeleton";
import { Tooltip } from "primereact/tooltip";
import { Tag } from "primereact/tag";
import { Menu } from "primereact/menu";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import {
  accessAPI,
  downloadFile,
  formatFecha,
  Boton,
  Titulo,
  Formulario,
  CampoInformacion,
  useIsMobile,
  TablaFiltrable
} from "../../../../shared";
import CardPagosPendientes from "./CardPagosPendientes";
import BotonUsuario from "./BotonUsuario";

const SEARCH_FIELDS_ACTIVIDADES = ["proceso", "actividadTipo", "pasoTipo", "rol"];
const SEARCH_FIELDS_FORMACIONES = ["_requerimientoNombre", "_normaOEsquema"];

export default function Auditor() {
  const { puedeVer: puedeVerPagos, puedeEditar: puedeEditarPagos } =
    usePermiso("admin.facturacion");
  const isMobile = useIsMobile();
  const [loader, setLoader] = useState(true);
  const [auditor, setAuditor] = useState();
  const [sideBarEditar, setSideBarEditar] = useState(false);
  const [loaderEditar, setLoaderEditar] = useState(false);
  const [formEditarKey, setFormEditarKey] = useState(0);
  const [loaderCompetencias, setLoaderCompetencias] = useState(false);

  const [formaciones, setFormaciones] = useState([]);
  const [actividades, setActividades] = useState([]);

  const [sideBarFormacion, setSideBarFormacion] = useState(false);
  const [loaderFormacion, setLoaderFormacion] = useState(false);
  const [loaderActivo, setLoaderActivo] = useState(false);
  const [formFormacionKey, setFormFormacionKey] = useState(0);
  const [descargandoArchivo, setDescargandoArchivo] = useState(null);
  const [firstFormaciones, setFirstFormaciones] = useState(0);

  // Registro de pago de una factura (al clickear el tag "Facturado")
  const [sideBarPago, setSideBarPago] = useState(false);
  const [facturaSeleccionada, setFacturaSeleccionada] = useState(null);
  const [loaderPago, setLoaderPago] = useState(false);
  const [formPagoKey, setFormPagoKey] = useState(0);
  const [descargandoFactura, setDescargandoFactura] = useState(false);
  const [menuRowSeleccionado, setMenuRowSeleccionado] = useState(null);
  const [descargandoPagoFila, setDescargandoPagoFila] = useState(null);
  const menuActividadRef = useRef(null);

  // Ingreso de la factura del colaborador desde el admin (mismo flujo que
  // cuando la sube el propio colaborador)
  const [sideBarFactura, setSideBarFactura] = useState(false);
  const [loaderFactura, setLoaderFactura] = useState(false);
  const [formFacturaKey, setFormFacturaKey] = useState(0);
  const [seleccionadosFactura, setSeleccionadosFactura] = useState([]);

  // Asignar pago manual a una actividad sin pago
  const [sideBarAsignar, setSideBarAsignar] = useState(false);
  const [asignarRow, setAsignarRow] = useState(null);
  const [tarifaPrefill, setTarifaPrefill] = useState({
    monto: null,
    monedaid: null
  });
  const [loaderAsignar, setLoaderAsignar] = useState(false);
  const [formAsignarKey, setFormAsignarKey] = useState(0);

  const toast = useRef(null);
  const { auditorId } = useParams();

  const camposEditables = [
    { campo: "nombre", etiqueta: "Nombre" },
    { campo: "apellido", etiqueta: "Apellido" },
    { campo: "cedula", etiqueta: "Cédula de identidad" },
    { campo: "telefono", etiqueta: "Teléfono" },
    { campo: "email", etiqueta: "Email" }
  ];

  function guardarEdicion(contenidoCampos) {
    setLoaderEditar(true);
    accessAPI(
      "PUT",
      `admin/auditor/${auditorId}`,
      contenidoCampos,
      () => {
        setAuditor((prev) => ({ ...prev, ...contenidoCampos }));
        setSideBarEditar(false);
        setLoaderEditar(false);
      },
      (respuesta) => {
        setLoaderEditar(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al guardar",
          sticky: true
        });
      }
    );
  }

  // Activa o desactiva el colaborador. Inactivo no aparece en los listados
  // ni puede ser asignado a pasos, pero conserva el acceso a la plataforma.
  function cambiarActivo() {
    setLoaderActivo(true);
    accessAPI(
      "PUT",
      `admin/auditor/${auditorId}/activo`,
      { activo: !auditor.activo },
      (respuesta) => {
        setLoaderActivo(false);
        setAuditor((prev) => ({ ...prev, activo: respuesta.activo }));
        toast.current?.show({
          severity: "success",
          summary: "Éxito",
          detail: respuesta.activo
            ? "Colaborador marcado como activo"
            : "Colaborador marcado como inactivo"
        });
      },
      (respuesta) => {
        setLoaderActivo(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cambiar el estado",
          sticky: true
        });
      }
    );
  }

  function getEditarFormConfig() {
    return {
      secciones: [
        {
          campos: camposEditables.map((c) => ({
            nombre: c.campo,
            tipoitemformularioid: c.campo === "email" ? 2 : 1,
            etiqueta: c.etiqueta,
            requerido: c.campo === "nombre" || c.campo === "apellido",
            respuesta: auditor[c.campo] || ""
          }))
        }
      ],
      botones: [
        {
          etiqueta: "Guardar cambios",
          callback: guardarEdicion,
          validar: true
        }
      ]
    };
  }

  function crearFormacion(camposFormulario, archivo) {
    setLoaderFormacion(true);
    accessAPI(
      "POST",
      `admin/auditor/formacion/${auditorId}`,
      camposFormulario,
      (respuesta) => {
        setLoaderFormacion(false);
        setSideBarFormacion(false);
        cargarAuditor();
      },
      (respuesta) => {
        setLoaderFormacion(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al agregar formación",
          sticky: true
        });
      },
      archivo
    );
  }

  const formacionFormConfig = {
    secciones: [
      {
        campos: [
          {
            nombre: "requerimientoFormacionColaboradorId",
            tipoitemformularioid: 5,
            etiqueta: "Requerimiento de formación",
            opciones: "admin/formacioncolaborador?dropdown=true",
            requerido: true,
            sinOpcionInicial: true
          },
          {
            nombre: "fecha",
            tipoitemformularioid: 7,
            etiqueta: "Fecha",
            requerido: true
          },
          {
            nombre: "normaId",
            tipoitemformularioid: 12,
            etiqueta:
              "Norma (si selecciona una norma no puede seleccionar un esquema)",
            opciones: "admin/norma",
            requerido: false,
            sinOpcionInicial: true
          },
          {
            nombre: "esquemaId",
            tipoitemformularioid: 12,
            etiqueta:
              "Esquema (si selecciona un esquema no puede seleccionar una norma)",
            opciones: "admin/config/esquema?dropdown=true",
            requerido: false,
            sinOpcionInicial: true
          },
          {
            nombre: "archivoId",
            tipoitemformularioid: 5,
            etiqueta: "Archivo existente",
            opciones: `admin/auditor/archivosformacion/${auditorId}`,
            requerido: false,
            sinOpcionInicial: true
          },
          {
            nombre: "archivo",
            tipoitemformularioid: 10,
            etiqueta: "O subir nuevo archivo de evidencia",
            requerido: false
          },
          {
            nombre: "notas",
            tipoitemformularioid: 11,
            etiqueta: "Notas",
            requerido: false
          }
        ]
      }
    ],
    botones: [
      {
        etiqueta: "Agregar formación",
        verde: true,
        callback: crearFormacion,
        validar: true
      }
    ]
  };

  function cargarAuditor() {
    accessAPI(
      "GET",
      `admin/auditor/${auditorId}?detalles=true`,
      null,
      (respuesta) => {
        setAuditor(respuesta);
        setFormaciones(respuesta.FormacionColaborador || []);
        const filas = (respuesta.ActividadPaso_x_Auditor || []).map((a) => {
            const pago = a.actividadpaso.pagosColaborador?.[0] || null;
            return {
              id: a.id,
              proceso:
                a.actividadpaso.actividad.proceso.nombre ||
                a.actividadpaso.actividad.proceso.nombrefantasia,
              actividadTipo: a.actividadpaso.actividad.actividadtipo.nombre,
              pasoTipo:
                a.actividadpaso.pasoTipo?.nombre ||
                "",
              rol: a.decisor ? "Decisor" : "En formación",
              fechaCierre: a.actividadpaso.actividad.fechafinalizacion,
              // La actividad está en el paso asignado a este colaborador
              enSuPaso: !!a.actividadpaso.actual,
              pagoId: pago?.id || null,
              pagoMonto: pago ? pago.monto : null,
              pagoMoneda: pago?.moneda?.simbolo || "",
              pagoMonedaId: pago?.moneda?.id ?? null,
              pagoMonedaNombre: pago?.moneda?.nombre || "",
              pagoEstado: pago ? pago.estado : null,
              pagoFacturado: !!pago?.facturaColaboradorId,
              facturaId: pago?.facturaColaborador?.id || null,
              facturaNotas: pago?.facturaColaborador?.notas || null,
              facturaArchivoId: pago?.facturaColaborador?.archivoid || null,
              facturaArchivoNombre:
                pago?.facturaColaborador?.archivo?.nombredescarga || "factura",
              comprobanteArchivoId:
                pago?.facturaColaborador?.comprobanteArchivoId || null,
              comprobanteNombre:
                pago?.facturaColaborador?.comprobante?.nombredescarga ||
                "comprobante"
            };
          });

        // Orden: pendientes en su paso, luego abiertas en otro paso, luego
        // finalizadas con pago sin cobrar, y el resto al final. Dentro de cada
        // grupo, por fecha de cierre descendente; sin fecha, por nombre del
        // proceso.
        const grupoOrden = (row) => {
          if (!row.fechaCierre) return row.enSuPaso ? 0 : 1;
          if (row.pagoId != null && row.pagoEstado !== "pagado") return 2;
          return 3;
        };
        filas.sort((a, b) => {
          const dif = grupoOrden(a) - grupoOrden(b);
          if (dif !== 0) return dif;
          const fechaA = a.fechaCierre ? new Date(a.fechaCierre).getTime() : 0;
          const fechaB = b.fechaCierre ? new Date(b.fechaCierre).getTime() : 0;
          if (fechaA !== fechaB) return fechaB - fechaA;
          return (a.proceso || "").localeCompare(b.proceso || "", "es");
        });

        setActividades(filas);
        document.title = `${respuesta.nombre} ${respuesta.apellido}`;
        setLoader(false);
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cargar colaborador",
          sticky: true
        });
        setLoader(false);
      }
    );
  }

  useEffect(() => {
    cargarAuditor();
    return () => (document.title = tituloBase());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditorId]);

  // Abre el sidebar de registro de pago para la factura del pago clickeado.
  function abrirPago(rowData) {
    setFacturaSeleccionada({
      facturaId: rowData.facturaId,
      archivoId: rowData.facturaArchivoId,
      archivoNombre: rowData.facturaArchivoNombre,
      notas: rowData.facturaNotas
    });
    setFormPagoKey((k) => k + 1);
    setSideBarPago(true);
  }

  // Abre el sidebar de asignar pago, pre-cargando la tarifa configurada.
  function abrirAsignarPago(row) {
    setAsignarRow(row);
    accessAPI(
      "GET",
      `admin/auditor/asignacion/${row.id}/tarifa`,
      null,
      (resp) => {
        setTarifaPrefill({
          monto: resp?.monto ?? null,
          monedaid: resp?.monedaid ?? null
        });
        setFormAsignarKey((k) => k + 1);
        setSideBarAsignar(true);
      },
      () => {
        // Si falla el prefill, abrir el form vacío igual.
        setTarifaPrefill({ monto: null, monedaid: null });
        setFormAsignarKey((k) => k + 1);
        setSideBarAsignar(true);
      }
    );
  }

  function submitAsignarPago(campos) {
    setLoaderAsignar(true);
    // El campo de precio (tipo 14) emite { precio, monedaId }; se aplana a las
    // claves monto/monedaid que espera el backend.
    accessAPI(
      "POST",
      `admin/auditor/asignacion/${asignarRow.id}/pago`,
      { monto: campos.pago?.precio ?? null, monedaid: campos.pago?.monedaId ?? null },
      () => {
        setLoaderAsignar(false);
        setSideBarAsignar(false);
        cargarAuditor();
        toast.current?.show({
          severity: "success",
          summary: "Pago asignado",
          detail: "El pago se asignó correctamente.",
          life: 3000
        });
      },
      (respuesta) => {
        setLoaderAsignar(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta?.[0]?.msg || "No se pudo asignar el pago.",
          sticky: true
        });
      }
    );
  }

  const formConfigAsignar = {
    secciones: [
      {
        campos: [
          {
            nombre: "pago",
            tipoitemformularioid: 14,
            etiqueta: "Monto",
            requerido: true,
            respuesta: {
              precio: tarifaPrefill.monto ?? null,
              monedaId: tarifaPrefill.monedaid ?? null
            }
          }
        ]
      }
    ],
    botones: [
      {
        etiqueta: "Asignar pago",
        callback: submitAsignarPago,
        validar: true
      }
    ]
  };

  // Descarga la factura seleccionada en el sidebar de cargar pago.
  function descargarFacturaSidebar() {
    if (!facturaSeleccionada?.archivoId) return;
    setDescargandoFactura(true);
    downloadFile(
      `admin/archivo/${facturaSeleccionada.archivoId}`,
      facturaSeleccionada.archivoNombre,
      () => setDescargandoFactura(false),
      () => {
        setDescargandoFactura(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "No se pudo descargar la factura.",
          sticky: true
        });
      }
    );
  }

  function descargarArchivoFactura(row) {
    if (!row?.facturaArchivoId) return;
    setDescargandoPagoFila(row.id);
    downloadFile(
      `admin/archivo/${row.facturaArchivoId}`,
      row.facturaArchivoNombre,
      () => setDescargandoPagoFila(null),
      () => {
        setDescargandoPagoFila(null);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "No se pudo descargar la factura.",
          sticky: true
        });
      }
    );
  }

  function descargarComprobante(row) {
    if (!row?.comprobanteArchivoId) return;
    setDescargandoPagoFila(row.id);
    downloadFile(
      `admin/archivo/${row.comprobanteArchivoId}`,
      row.comprobanteNombre,
      () => setDescargandoPagoFila(null),
      () => {
        setDescargandoPagoFila(null);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "No se pudo descargar el comprobante.",
          sticky: true
        });
      }
    );
  }

  // Opciones del menú contextual de un pago, según su estado. Descargar la
  // factura y el comprobante alcanza con lectura de facturación; el resto pide
  // full.
  function getMenuActividadItems() {
    const row = menuRowSeleccionado;
    if (!row) return [];
    const items = [];
    if (puedeEditarPagos && !row.pagoId) {
      items.push({
        label: "Asignar pago",
        icon: "pi pi-dollar",
        command: () => abrirAsignarPago(row)
      });
    }
    if (row.facturaArchivoId) {
      items.push({
        label: "Descargar factura",
        icon: "pi pi-download",
        command: () => descargarArchivoFactura(row)
      });
    }
    if (row.comprobanteArchivoId) {
      items.push({
        label: "Descargar comprobante de pago",
        icon: "pi pi-download",
        command: () => descargarComprobante(row)
      });
    }
    if (puedeEditarPagos && row.pagoFacturado && row.pagoEstado !== "pagado") {
      items.push({
        label: "Cargar pago",
        icon: "pi pi-upload",
        command: () => abrirPago(row)
      });
    }
    // Ingresar la factura en nombre del colaborador: solo si el pago está
    // pendiente, sin factura ni pagado.
    if (puedeEditarPagos && pagoEsDescartable(row)) {
      items.push({
        label: "Ingresar factura",
        icon: "pi pi-file-import",
        command: () => abrirIngresarFactura()
      });
    }
    // "No aplica pago": solo si el pago está pendiente, sin factura ni pagado.
    if (puedeEditarPagos && pagoEsDescartable(row)) {
      items.push({
        label: "No aplica pago",
        icon: "pi pi-ban",
        command: () => noAplicaPago(row)
      });
    }
    return items;
  }

  // Un pago se puede descartar si existe, no está facturado ni pagado.
  function pagoEsDescartable(row) {
    return (
      row.pagoId != null &&
      row.pagoEstado !== "pagado" &&
      !row.pagoFacturado
    );
  }

  // ==========================================================================
  // Ingreso de la factura del colaborador desde el admin. Espeja el sidebar de
  // subir factura de la plataforma de colaboradores: mismas actividades
  // facturables, mismo formulario y mismo flujo posterior en la API.
  // ==========================================================================

  // Actividades con pago pendiente sin facturar (los mismos criterios que la
  // plataforma del colaborador).
  const pendientesFactura = actividades.filter(pagoEsDescartable);

  const descripcionPagoFila = (row) => {
    const montoTxt = `${row.pagoMoneda} ${row.pagoMonto}`.trim();
    return `${row.proceso}${row.actividadTipo ? " · " + row.actividadTipo : ""}${
      row.pasoTipo ? " / " + row.pasoTipo : ""
    } — ${montoTxt} · ${row.rol}`;
  };

  // Formulario dinámico: filepicker + un checkbox (tildado) por pago pendiente.
  const formConfigFactura = {
    secciones: [
      {
        campos: [
          {
            nombre: "archivo",
            tipoitemformularioid: 10,
            etiqueta: "Archivo de la factura"
          },
          ...pendientesFactura.map((row) => ({
            nombre: `pago_${row.pagoId}`,
            tipoitemformularioid: 6,
            etiqueta: descripcionPagoFila(row),
            respuesta: true
          })),
          {
            nombre: "notas",
            tipoitemformularioid: 11,
            etiqueta: "Notas"
          }
        ]
      }
    ],
    botones: [
      {
        // validar: true es necesario para que Formulario pase el archivo como
        // segundo parámetro del callback (sin validar no lo entrega). Ningún
        // campo es `requerido`, así que la validación siempre pasa; el archivo
        // y la selección se chequean manualmente en submitFactura.
        etiqueta: "Subir factura",
        callback: submitFactura,
        validar: true
      }
    ]
  };

  function submitFactura(campos, archivo) {
    const pagoIds = Object.keys(campos)
      .filter((k) => k.startsWith("pago_") && campos[k])
      .map((k) => parseInt(k.slice("pago_".length)));

    if (!archivo) {
      toast.current?.show({
        severity: "warn",
        summary: "Falta el archivo",
        detail: "Adjuntá el archivo de la factura.",
        life: 3000
      });
      return;
    }
    if (!pagoIds.length) {
      toast.current?.show({
        severity: "warn",
        summary: "Sin pagos",
        detail: "Seleccioná al menos un pago pendiente.",
        life: 3000
      });
      return;
    }

    setLoaderFactura(true);
    const datos = { pagoIds };
    if (campos.notas?.trim()) {
      datos.notas = campos.notas.trim();
    }
    accessAPI(
      "POST",
      `admin/auditor/${auditorId}/factura`,
      datos,
      () => {
        setLoaderFactura(false);
        setSideBarFactura(false);
        cargarAuditor();
        toast.current?.show({
          severity: "success",
          summary: "Factura ingresada",
          detail: "La factura se registró correctamente.",
          life: 3000
        });
      },
      (respuesta) => {
        setLoaderFactura(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta?.[0]?.msg || "No se pudo subir la factura.",
          sticky: true
        });
      },
      archivo
    );
  }

  // Deriva los pagos tildados desde el contenido del Formulario y actualiza el
  // total en vivo.
  function handleCambioFactura(contenido) {
    setSeleccionadosFactura(
      pendientesFactura
        .filter((row) => contenido[`pago_${row.pagoId}`])
        .map((row) => row.pagoId)
    );
  }

  function abrirIngresarFactura() {
    // Arrancan todos tildados, igual que los checkboxes del formulario.
    setSeleccionadosFactura(pendientesFactura.map((row) => row.pagoId));
    setFormFacturaKey((k) => k + 1);
    setSideBarFactura(true);
  }

  // Total en vivo de lo tildado, agrupado por moneda (una factura debe ser de
  // una sola moneda; si hay varias, se muestran separadas como aviso).
  const totalesPorMonedaFactura = {};
  pendientesFactura
    .filter((row) => seleccionadosFactura.includes(row.pagoId))
    .forEach((row) => {
      const sim = row.pagoMoneda || "";
      totalesPorMonedaFactura[sim] =
        (totalesPorMonedaFactura[sim] || 0) + row.pagoMonto;
    });
  const resumenTotalesFactura =
    Object.entries(totalesPorMonedaFactura)
      .map(([sim, tot]) => `${sim} ${tot}`.trim())
      .join("  ·  ") || "—";

  // Elimina el registro de pago pendiente (con confirmación).
  function noAplicaPago(row) {
    confirmDialog({
      message:
        "¿Marcar que no aplica pago para este paso? Se eliminará el registro de pago pendiente.",
      header: "Confirmar",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Sí, no aplica",
      rejectLabel: "Cancelar",
      acceptClassName: "p-button-danger",
      accept: () => {
        accessAPI(
          "DELETE",
          `admin/auditor/pago/${row.pagoId}`,
          null,
          () => {
            cargarAuditor();
            toast.current?.show({
              severity: "success",
              summary: "Listo",
              detail: "El pago se eliminó correctamente.",
              life: 3000
            });
          },
          (respuesta) => {
            toast.current?.show({
              severity: "error",
              summary: "Error",
              detail: respuesta?.[0]?.msg || "No se pudo eliminar el pago.",
              sticky: true
            });
          }
        );
      }
    });
  }

  // ¿La fila tiene alguna acción de pago disponible? (para mostrar el menú)
  function tieneAccionesPago(row) {
    // Con lectura, las únicas acciones son las descargas
    const descargas = !!row.facturaArchivoId || !!row.comprobanteArchivoId;
    if (!puedeEditarPagos) return descargas;
    return (
      !row.pagoId ||
      descargas ||
      (row.pagoFacturado && row.pagoEstado !== "pagado") ||
      pagoEsDescartable(row)
    );
  }

  // Sube el comprobante y marca la factura (y sus actividades) como pagadas.
  function submitPago(campos, archivo) {
    if (!archivo) {
      toast.current?.show({
        severity: "warn",
        summary: "Falta el comprobante",
        detail: "Adjuntá el comprobante de pago.",
        life: 3000
      });
      return;
    }
    setLoaderPago(true);
    accessAPI(
      "POST",
      `admin/auditor/factura/${facturaSeleccionada.facturaId}/pago`,
      {},
      () => {
        setLoaderPago(false);
        setSideBarPago(false);
        cargarAuditor();
        toast.current?.show({
          severity: "success",
          summary: "Pago registrado",
          detail: "La factura y sus actividades quedaron como pagadas.",
          life: 3000
        });
      },
      (respuesta) => {
        setLoaderPago(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta?.[0]?.msg || "No se pudo registrar el pago.",
          sticky: true
        });
      },
      archivo
    );
  }

  const formConfigPago = {
    secciones: [
      {
        campos: [
          {
            nombre: "archivo",
            tipoitemformularioid: 10,
            etiqueta: "Comprobante de pago"
          }
        ]
      }
    ],
    botones: [
      {
        etiqueta: "Registrar pago",
        callback: submitPago,
        validar: true
      }
    ]
  };

  // _requerimientoNombre / _normaOEsquema: campos planos derivados para que
  // TablaFiltrable pueda buscar sobre datos anidados.
  const formacionesDatos = (formaciones || []).map((f) => ({
    ...f,
    _requerimientoNombre: f.RequerimientoFormacionColaborador?.nombre,
    _normaOEsquema: f.norma?.numero || f.esquema?.etiqueta
  }));

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <ConfirmDialog />
      <Menu model={getMenuActividadItems()} popup ref={menuActividadRef} />

      <Sidebar
        visible={sideBarPago}
        onHide={() => setSideBarPago(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <div css={{ marginBottom: "50px" }}>
          <Titulo>Registrar pago de factura</Titulo>
          <div css={{ marginBottom: "1.5rem" }}>
            <Boton
              azul
              loader={descargandoFactura}
              callback={descargarFacturaSidebar}
              disabled={!facturaSeleccionada?.archivoId}
            >
              <i className="pi pi-download" css={{ marginRight: "0.4em" }} />
              Descargar factura
            </Boton>
          </div>
          {facturaSeleccionada?.notas && (
            <div css={{ marginBottom: "1.5rem" }}>
              <h4 css={{ marginBottom: "0.5rem" }}>Notas del colaborador</h4>
              <p css={{ margin: 0, whiteSpace: "pre-wrap" }}>
                {facturaSeleccionada.notas}
              </p>
            </div>
          )}
          <Formulario
            key={formPagoKey}
            config={formConfigPago}
            loader={loaderPago}
          />
        </div>
      </Sidebar>

      <Sidebar
        visible={sideBarFactura}
        onHide={() => setSideBarFactura(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <div css={{ marginBottom: "50px" }}>
          <Titulo>Ingresar factura</Titulo>
          {pendientesFactura.length === 0 ? (
            <p css={{ color: "var(--grisTextos)" }}>
              No hay pagos pendientes para facturar.
            </p>
          ) : (
            <>
              <div
                css={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "1rem",
                  padding: "0.6rem 0.8rem",
                  borderRadius: "6px",
                  backgroundColor: "var(--grisFondo, #f4f5f7)"
                }}
              >
                <span css={{ color: "var(--grisTextos)" }}>
                  Total a facturar
                </span>
                <span css={{ fontWeight: 700 }}>{resumenTotalesFactura}</span>
              </div>
              <Formulario
                key={formFacturaKey}
                config={formConfigFactura}
                loader={loaderFactura}
                onCambio={handleCambioFactura}
              />
            </>
          )}
        </div>
      </Sidebar>

      <Sidebar
        visible={sideBarAsignar}
        onHide={() => setSideBarAsignar(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <div css={{ marginBottom: "50px" }}>
          <Titulo>Asignar pago</Titulo>
          <Formulario
            key={formAsignarKey}
            config={formConfigAsignar}
            loader={loaderAsignar}
          />
        </div>
      </Sidebar>

      <Sidebar
        visible={sideBarEditar}
        onHide={() => setSideBarEditar(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Editar colaborador</Titulo>
        {auditor && (
          <Formulario
            key={formEditarKey}
            config={getEditarFormConfig()}
            loader={loaderEditar}
          />
        )}
      </Sidebar>

      <Sidebar
        visible={sideBarFormacion}
        onHide={() => setSideBarFormacion(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Agregar evidencia de formación</Titulo>
        <Formulario
          key={formFormacionKey}
          config={formacionFormConfig}
          loader={loaderFormacion}
        />
      </Sidebar>

      {loader && (
        <div
          css={{
            marginTop: "2em",
            display: "flex",
            flexDirection: "column",
            gap: "1em"
          }}
        >
          {/* Título */}
          <div
            css={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}
          >
            <div css={{ display: "flex", alignItems: "center", gap: "1em" }}>
              <Skeleton width="2rem" height="2rem" shape="circle" />
              <Skeleton width="15rem" height="2rem" />
            </div>
            <Skeleton width="14rem" height="2.5rem" />
          </div>
          {/* Card Detalles */}
          <Card>
            <Skeleton
              width="6rem"
              height="1.5rem"
              css={{ marginBottom: "1.5em" }}
            />
            <div className="grid">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="col-12 md:col-4">
                  <Skeleton
                    width="50%"
                    height="0.8rem"
                    css={{ marginBottom: "0.5rem" }}
                  />
                  <Skeleton width="80%" height="1.2rem" />
                </div>
              ))}
            </div>
          </Card>
          {/* Card Formación */}
          <Card>
            <Skeleton
              width="12rem"
              height="1.5rem"
              css={{ marginBottom: "1.5em" }}
            />
            <div
              css={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
            >
              <Skeleton width="100%" height="3rem" />
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} width="100%" height="3.5rem" />
              ))}
            </div>
          </Card>
        </div>
      )}

      {!loader && auditor && (
        <div>
          <Titulo
            urlVolver="/proveedores/auditores"
            etiqueta={auditor.activo === false ? "Inactivo" : undefined}
            error={auditor.activo === false}
            botones={
              <>
                <BotonUsuario auditor={auditor} />
                <Boton
                  rojo={auditor.activo}
                  verde={!auditor.activo}
                  transparente
                  loader={loaderActivo}
                  confirmacion={
                    auditor.activo
                      ? "¿Marcar al colaborador como inactivo? No aparecerá en los listados ni podrá ser asignado a pasos, pero conservará el acceso a la plataforma."
                      : "¿Marcar al colaborador como activo?"
                  }
                  callback={cambiarActivo}
                >
                  {auditor.activo ? "Marcar como inactivo" : "Marcar como activo"}
                </Boton>
                <Boton
                  transparente
                  loader={loaderCompetencias}
                  callback={() => {
                    setLoaderCompetencias(true);
                    downloadFile(
                      `admin/auditor/competencias/${auditorId}`,
                      `Competencias - ${auditor.nombre} ${auditor.apellido}.xlsx`,
                      () => setLoaderCompetencias(false),
                      () => {
                        setLoaderCompetencias(false);
                        toast.current?.show({
                          severity: "error",
                          summary: "Error",
                          detail: "Error al descargar competencias",
                          sticky: true
                        });
                      }
                    );
                  }}
                >
                  Resumen de competencias
                </Boton>
              </>
            }
          >
            {auditor.nombre} {auditor.apellido}
          </Titulo>

          <Card>
            <Titulo
              chico
              botones={
                <Boton
                  texto
                  negro
                  callback={() => {
                    setFormEditarKey((k) => k + 1);
                    setSideBarEditar(true);
                  }}
                >
                  <i className="pi pi-pencil" />
                </Boton>
              }
            >
              Detalles
            </Titulo>
            <div className="grid">
              <CampoInformacion nombreCampo="Nombre">
                {auditor.nombre}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Apellido">
                {auditor.apellido}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Cédula de identidad">
                {auditor.cedula}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Teléfono">
                {auditor.telefono}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Email">
                {auditor.email}
              </CampoInformacion>
            </div>
          </Card>

          <CardPagosPendientes actividades={actividades} />

          <Card css={{ marginTop: "1em" }}>
            <Titulo chico>Actividades</Titulo>
            <TablaFiltrable
              value={actividades}
              searchFields={SEARCH_FIELDS_ACTIVIDADES}
              anchoBusqueda="33%"
              nombreArchivo="actividades_colaborador"
              stripedRows
              size="small"
              emptyMessage="El colaborador no tiene actividades"
            >
              <Column
                header="Actividad"
                body={(rowData) =>
                  isMobile
                    ? rowData.proceso
                    : [rowData.proceso, rowData.actividadTipo, rowData.pasoTipo]
                        .filter(Boolean)
                        .join(" - ")
                }
              />
              {!isMobile && (
                <Column header="Rol" field="rol" style={{ width: "120px" }} />
              )}
              {/* Los pagos entran en el permiso de facturación: con lectura
                  se ven y se descargan sus archivos; el resto de las acciones
                  de cada fila pide full */}
              {puedeVerPagos && (
              <Column
                header="Pago"
                body={(rowData) => {
                  if (rowData.pagoMonto == null) return "-";
                  let severity = "warning";
                  let etiqueta = "Pendiente";
                  if (rowData.pagoEstado === "pagado") {
                    severity = "success";
                    etiqueta = "Pago";
                  } else if (rowData.pagoFacturado) {
                    severity = "danger";
                    etiqueta = "Facturado";
                  }
                  return (
                    <div
                      css={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        flexWrap: "wrap"
                      }}
                    >
                      <span>
                        {`${rowData.pagoMoneda} ${rowData.pagoMonto}`.trim()}
                      </span>
                      <Tag severity={severity} css={{ fontSize: "0.7rem" }}>
                        {etiqueta}
                      </Tag>
                    </div>
                  );
                }}
                style={{ width: "170px" }}
              />
              )}
              <Column
                header={isMobile ? "" : "Estado"}
                body={(rowData) => {
                  const cerrada = !!rowData.fechaCierre;
                  if (isMobile) {
                    if (cerrada) {
                      return (
                        <i
                          className="pi pi-check-circle"
                          title="Cerrada"
                          css={{ color: "var(--verde)" }}
                        />
                      );
                    }
                    return (
                      <i
                        className="pi pi-exclamation-circle"
                        title={rowData.enSuPaso ? "Pendiente" : "En otro paso"}
                        css={{
                          color: rowData.enSuPaso
                            ? "var(--amarillo)"
                            : "var(--grisTextos)"
                        }}
                      />
                    );
                  }
                  if (cerrada) {
                    return formatFecha(rowData.fechaCierre);
                  }
                  if (rowData.enSuPaso) {
                    return (
                      <span
                        css={{
                          color: "var(--amarillo)",
                          fontWeight: "bold"
                        }}
                      >
                        Pendiente
                      </span>
                    );
                  }
                  return (
                    <span css={{ color: "var(--grisTextos)" }}>
                      En otro paso
                    </span>
                  );
                }}
                style={{ width: isMobile ? "3rem" : "120px" }}
              />
              {puedeVerPagos && (
              <Column
                body={(rowData) =>
                  descargandoPagoFila === rowData.id ? (
                    <i className="pi pi-spin pi-spinner" />
                  ) : tieneAccionesPago(rowData) ? (
                    <Boton
                      texto
                      negro
                      chico
                      callback={(e) => {
                        setMenuRowSeleccionado(rowData);
                        menuActividadRef.current.toggle(e);
                      }}
                    >
                      <i className="pi pi-ellipsis-v" />
                    </Boton>
                  ) : null
                }
                style={{ width: "3rem", textAlign: "center" }}
              />
              )}
            </TablaFiltrable>
          </Card>

          <Card css={{ marginTop: "1em" }}>
            <Titulo
              chico
              botones={
                <Boton
                  texto
                  negro
                  callback={() => {
                    setFormFormacionKey((k) => k + 1);
                    setSideBarFormacion(true);
                  }}
                >
                  <i className="pi pi-plus" />
                </Boton>
              }
            >
              Evidencia de formación
            </Titulo>
            <TablaFiltrable
              value={formacionesDatos}
              searchFields={SEARCH_FIELDS_FORMACIONES}
              anchoBusqueda="33%"
              nombreArchivo="formacion_colaborador"
              stripedRows
              size="small"
              first={firstFormaciones}
              onPage={(e) => setFirstFormaciones(e.first)}
              emptyMessage="El colaborador no tiene formaciones"
            >
              <Column
                header="Requerimiento"
                body={(rowData) => (
                  <div>
                    <span>
                      {rowData.RequerimientoFormacionColaborador.nombre}
                    </span>
                    {!rowData.RequerimientoFormacionColaborador.activo && (
                      <span
                        css={{
                          color: "var(--rojo)",
                          marginLeft: "0.5em"
                        }}
                      >
                        (Inactivo)
                      </span>
                    )}
                  </div>
                )}
              />
              <Column
                header="Norma o esquema"
                body={(rowData) =>
                  rowData.norma?.numero || rowData.esquema?.etiqueta || ""
                }
              />
              <Column
                header="Fecha"
                body={(rowData) => formatFecha(rowData.fecha)}
                style={{ width: "120px" }}
              />
              <Column
                body={(rowData) => {
                  if (!rowData.notas) return null;
                  const tooltipId = `notas-formacion-${rowData.id}`;
                  return (
                    <>
                      <Tooltip
                        target={`#${tooltipId}`}
                        content={rowData.notas}
                        position="top"
                      />
                      <i
                        id={tooltipId}
                        className="pi pi-info-circle"
                        style={{
                          color: "var(--amarillo)",
                          fontSize: "1.2rem",
                          cursor: "pointer"
                        }}
                      />
                    </>
                  );
                }}
                style={{ width: "60px", textAlign: "center" }}
              />
              <Column
                body={(rowData) => {
                  if (!rowData.archivo) return null;
                  const descargando = descargandoArchivo === rowData.archivo.id;
                  return (
                    <div
                      css={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "0.5em"
                      }}
                    >
                      {descargando && <i className="pi pi-spin pi-spinner" />}
                      <Boton
                        texto
                        negro
                        chico
                        callback={() => {
                          setDescargandoArchivo(rowData.archivo.id);
                          downloadFile(
                            `admin/archivo/${rowData.archivo.id}`,
                            rowData.archivo.nombredescarga,
                            () => setDescargandoArchivo(null),
                            (respuesta) => {
                              setDescargandoArchivo(null);
                              toast.current?.show({
                                severity: "error",
                                summary: "Error",
                                detail:
                                  respuesta[0]?.msg ||
                                  "Error al descargar archivo",
                                sticky: true
                              });
                            }
                          );
                        }}
                      >
                        <i className="pi pi-download" />
                      </Boton>
                    </div>
                  );
                }}
                style={{ width: "60px", textAlign: "center" }}
              />
            </TablaFiltrable>
          </Card>
        </div>
      )}
    </>
  );
}
